<template>
   <dashboard-layout>
      <v-container fluid class="down-top-padding py-0">
         <BaseBreadcrumb
            :title="page.title"
            :icon="page.icon"
            :breadcrumbs="breadcrumbs"
         ></BaseBreadcrumb>

         <v-card class="mb-4">
            <v-card-text class="d-flex align-center py-3">
               <v-switch
                  v-model="moduleEnabled"
                  :loading="moduleLoading"
                  color="primary"
                  hide-details
                  class="mt-0 pt-0"
                  @change="toggleModule"
               ></v-switch>
               <span class="ml-2 font-weight-medium">
                  Nomination Module {{ moduleEnabled ? 'Enabled' : 'Disabled' }}
               </span>
            </v-card-text>
         </v-card>

         <v-card>
            <v-toolbar flat color="white">
               <v-toolbar-title>Nominations</v-toolbar-title>
               <v-divider class="mx-4" inset vertical></v-divider>
               <v-spacer></v-spacer>
               <v-btn color="primary" dark class="mr-2" @click="openNew">
                  <v-icon left small>mdi-plus</v-icon>New Item
               </v-btn>
               <v-btn color="primary" outlined @click="loadItems" :loading="loading">Refresh</v-btn>
            </v-toolbar>

            <v-data-table
               :headers="headers"
               :items="items"
               :loading="loading"
               class="elevation-0"
            >
               <template v-slot:item.no="{ index }">
                  {{ index + 1 }}
               </template>
               <template v-slot:item.isenabled="{ item }">
                  <v-chip :color="item.isenabled ? 'green lighten-4' : 'red lighten-4'" :text-color="item.isenabled ? 'green darken-3' : 'red darken-3'" x-small>
                     {{ item.isenabled ? 'Yes' : 'No' }}
                  </v-chip>
               </template>
               <template v-slot:item.period="{ item }">
                  {{ formatDate(item.datestart) }} - {{ formatDate(item.dateend) }}
               </template>
               <template v-slot:item.action="{ item }">
                  <v-btn x-small text color="primary" @click="openEdit(item)">Edit</v-btn>
                  <v-btn x-small text color="error" @click="confirmDelete(item)">Delete</v-btn>
                  <v-btn x-small text color="info" @click="viewResult(item)">Result</v-btn>
               </template>
            </v-data-table>

         </v-card>
      </v-container>

      <!-- Add / Edit Dialog -->
      <v-dialog v-model="formDialog" max-width="800" persistent>
         <v-card>
            <v-toolbar flat color="primary" dark>
               <v-toolbar-title>{{ form.id ? 'Edit' : 'New' }} Nomination Position</v-toolbar-title>
               <v-spacer></v-spacer>
               <v-btn icon @click="formDialog = false">
                  <v-icon>mdi-close</v-icon>
               </v-btn>
            </v-toolbar>
            <v-card-text class="pa-4">
               <v-alert v-if="formError" type="error" text dense>{{ formError }}</v-alert>
               <v-text-field v-model="form.title" label="Title" outlined dense></v-text-field>
               <v-text-field v-model="form.summary" label="Summary" outlined dense></v-text-field>
               <v-textarea v-model="form.description" label="Description" outlined dense rows="3"></v-textarea>

               <v-row dense>
                  <v-col cols="12" md="6">
                     <v-text-field v-model="form.datestart" label="Date Start" type="datetime-local" outlined dense></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                     <v-text-field v-model="form.dateend" label="Date End" type="datetime-local" outlined dense></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                     <v-text-field v-model="form.datewithdrawstart" label="Withdraw Start" type="datetime-local" outlined dense></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                     <v-text-field v-model="form.datewithdrawend" label="Withdraw End" type="datetime-local" outlined dense></v-text-field>
                  </v-col>
               </v-row>

               <v-select
                  v-model="form.membertypes"
                  :items="membertypeOptions"
                  label="Member Types"
                  multiple
                  chips
                  outlined
                  dense
               ></v-select>

               <v-select
                  v-model="form.nomzone"
                  :items="zones"
                  item-text="zonename"
                  item-value="id"
                  label="Zone"
                  outlined
                  dense
                  clearable
               ></v-select>

               <v-row dense>
                  <v-col cols="12" md="4">
                     <v-switch v-model="form.isenabled" label="Enabled" color="primary"></v-switch>
                  </v-col>
                  <v-col cols="12" md="4">
                     <v-switch v-model="form.isonlycouncilmembers" label="Council Members Only" color="primary"></v-switch>
                  </v-col>
                  <v-col cols="12" md="4">
                     <v-switch v-model="form.isnonmandatory" label="Optional" color="primary"></v-switch>
                  </v-col>
               </v-row>
            </v-card-text>
            <v-card-actions class="px-4 pb-4">
               <v-spacer></v-spacer>
               <v-btn text @click="formDialog = false">Cancel</v-btn>
               <v-btn color="primary" :loading="formSaving" @click="saveForm">Save</v-btn>
            </v-card-actions>
         </v-card>
      </v-dialog>

      <!-- Delete Confirm -->
      <v-dialog v-model="deleteDialog" max-width="420">
         <v-card>
            <v-card-title>Delete Nomination Position</v-card-title>
            <v-card-text>Are you sure you want to delete "{{ deleteTarget ? deleteTarget.title : '' }}"?</v-card-text>
            <v-card-actions class="px-4 pb-4">
               <v-spacer></v-spacer>
               <v-btn text @click="deleteDialog = false">Cancel</v-btn>
               <v-btn color="red" dark :loading="deleting" @click="doDelete">Delete</v-btn>
            </v-card-actions>
         </v-card>
      </v-dialog>

      <!-- Result Dialog -->
      <v-dialog v-model="resultDialog" max-width="1100">
         <v-card>
            <v-toolbar flat color="primary" dark>
               <v-toolbar-title>{{ resultTitle }} - Result</v-toolbar-title>
               <v-spacer></v-spacer>
               <v-btn icon @click="resultDialog = false">
                  <v-icon>mdi-close</v-icon>
               </v-btn>
            </v-toolbar>

            <v-card-text class="pa-4">
               <v-alert v-if="resultError" type="error" text dense>{{ resultError }}</v-alert>

            <v-progress-linear v-if="resultLoading" indeterminate color="primary" class="mb-3"></v-progress-linear>

            <div v-if="!resultLoading && activeNominations.length === 0 && withdrawnNominations.length === 0" class="text-center grey--text pa-6">
               No nominee has been submitted for this position
            </div>

            <!-- Active Nominations -->
            <div v-if="activeNominations.length > 0">
               <div class="subtitle-2 font-weight-bold mb-2 mt-2">Active Nominations ({{ activeNominations.length }})</div>
               <div
                  v-for="(group, index) in activeNominations"
                  :key="'active-' + group.nom_id"
                  class="mb-3 pa-3"
                  style="border:1px solid #c8e6c9; border-radius:6px;"
               >
                  <div class="d-flex align-center justify-space-between flex-wrap">
                     <div class="d-flex align-center">
                        <v-avatar size="28" color="blue lighten-4" class="mr-2">
                           <span class="blue--text text--darken-3 font-weight-bold caption">{{ index + 1 }}</span>
                        </v-avatar>
                        <span class="text-subtitle-1 font-weight-bold">— {{ group.nom_name }}</span>
                        <v-chip color="green lighten-4" text-color="green darken-3" x-small class="ml-2">
                           <v-icon left x-small>mdi-poll</v-icon>
                           {{ group.vote_count }}
                        </v-chip>
                     </div>
                     <div>
                        <v-btn x-small text color="primary" @click="resendEmail('nominee', group.rows[0])">Re-send Email</v-btn>
                     </div>
                  </div>
                  <div class="caption grey--text text--darken-2 my-2">
                     Nominee Consent (Proposer: {{ group.rows[0].prop_name }}):
                     <strong>{{ group.nom_consent == 1 ? 'Yes' : 'No' }}</strong>
                     @ {{ group.nom_dateconsent || '-' }}
                     <span class="ml-2">TAC: {{ group.nom_tac || '-' }}</span>
                  </div>
                  <v-simple-table dense>
                     <template v-slot:default>
                        <thead>
                           <tr>
                              <th>No</th>
                              <th>Proposer</th>
                              <th>TAC</th>
                              <th>Consent</th>
                              <th>Date</th>
                              <th>Seconder</th>
                              <th>TAC</th>
                              <th>Consent</th>
                              <th>Date</th>
                           </tr>
                        </thead>
                        <tbody>
                           <tr v-for="(row, rowIndex) in group.rows" :key="row.id">
                              <td>{{ rowIndex + 1 }}</td>
                              <td>
                                 {{ row.prop_name }}
                                 <div>
                                    <v-btn x-small text color="primary" @click="resendEmail('proposer', row)">
                                       <v-icon left x-small>mdi-email-fast-outline</v-icon>Re-send Email
                                    </v-btn>
                                 </div>
                              </td>
                              <td>{{ row.prop_tac || '-' }}</td>
                              <td>
                                 <v-chip :color="row.prop_consent == 1 ? 'green lighten-4' : 'red lighten-4'" :text-color="row.prop_consent == 1 ? 'green darken-3' : 'red darken-3'" x-small>
                                    {{ row.prop_consent == 1 ? 'Yes' : 'No' }}
                                 </v-chip>
                              </td>
                              <td>{{ row.prop_dateconsent || '-' }}</td>
                              <td>
                                 {{ row.sec_name }}
                                 <div>
                                    <v-btn x-small text color="primary" @click="resendEmail('seconder', row)">
                                       <v-icon left x-small>mdi-email-fast-outline</v-icon>Re-send Email
                                    </v-btn>
                                 </div>
                              </td>
                              <td>{{ row.sec_tac || '-' }}</td>
                              <td>
                                 <v-chip :color="row.sec_consent == 1 ? 'green lighten-4' : 'red lighten-4'" :text-color="row.sec_consent == 1 ? 'green darken-3' : 'red darken-3'" x-small>
                                    {{ row.sec_consent == 1 ? 'Yes' : 'No' }}
                                 </v-chip>
                              </td>
                              <td>{{ row.sec_dateconsent || '-' }}</td>
                           </tr>
                        </tbody>
                     </template>
                  </v-simple-table>
               </div>
            </div>

            <!-- Withdrawn Nominations -->
            <div v-if="withdrawnNominations.length > 0" class="mt-6">
               <v-divider class="mb-3"></v-divider>
               <div class="subtitle-2 font-weight-bold mb-2 red--text text--darken-2">
                  <v-icon small color="red darken-2" class="mr-1">mdi-account-remove-outline</v-icon>
                  Withdrawn ({{ withdrawnNominations.length }})
               </div>
               <div
                  v-for="(item, index) in withdrawnNominations"
                  :key="'withdrawn-' + item.id"
                  class="mb-3 pa-3"
                  style="border:1px solid #ffcdd2; border-radius:6px; opacity:0.75;"
               >
                  <div class="d-flex align-center flex-wrap">
                     <v-avatar size="28" color="grey" class="mr-2">
                        <span class="white--text caption font-weight-bold">{{ index + 1 }}</span>
                     </v-avatar>
                     <span class="font-weight-medium mr-2" style="text-decoration:line-through;">� {{ item.nom_name }}</span>
                     <v-chip x-small color="red lighten-4" text-color="red darken-3">
                        Withdrawn {{ formatDate(item.nom_datewithdrawal) }}
                     </v-chip>
                     <v-spacer></v-spacer>
                     <v-btn x-small text color="primary" @click="cancelWithdrawal(item)">
                        <v-icon left x-small>mdi-undo</v-icon>Cancel Withdrawal
                     </v-btn>
                  </div>
                  <div class="caption mt-1 ml-9 grey--text">
                     Proposer: {{ item.prop_name }} &nbsp;|&nbsp; Seconder: {{ item.sec_name }}
                  </div>
               </div>
            </div>

            <!-- Pending Consent -->
             <div v-if="pendingNominations.length > 0" class="mt-6">
               <v-divider class="mb-3"></v-divider>
               <div class="subtitle-2 font-weight-bold mb-2 orange--text text--darken-3">
                  <v-icon small color="orange darken-3" class="mr-1">mdi-clock-alert-outline</v-icon>
                  Pending Consent ({{ pendingNominations.length }})
               </div>
               <div
                  v-for="(group, index) in pendingNominations"
                  :key="'pending-' + group.nom_id"
                  class="mb-3 pa-3"
                  style="border:1px solid #ffe0b2; border-radius:6px; opacity:0.85;"
               >
                  <div class="d-flex align-center mb-2">
                     <v-avatar size="28" color="orange lighten-4" class="mr-2">
                        <span class="orange--text text--darken-3 font-weight-bold caption">{{ index + 1 }}</span>
                     </v-avatar>
                     <span class="text-subtitle-1 font-weight-bold">{{ group.nom_name }}</span>
                  </div>
                  <div
                     v-for="(row, rIndex) in group.rows"
                     :key="'pending-row-' + row.id"
                     class="ml-9 mb-2 pa-2"
                     style="background:#fff8e1; border-radius:4px;"
                  >
                     <div class="caption grey--text text--darken-2 mb-1">
                        Proposer: {{ row.prop_name }} &nbsp;|&nbsp; Seconder: {{ row.sec_name }}
                     </div>
                     <div class="d-flex align-center flex-wrap" style="gap:8px;">
                        <v-chip x-small :color="row.nom_consent == 1 ? 'green lighten-4' : 'orange lighten-4'" :text-color="row.nom_consent == 1 ? 'green darken-3' : 'orange darken-3'">
                           Nominee: {{ row.nom_consent == 1 ? 'Yes' : 'Pending' }}
                        </v-chip>
                        <v-chip x-small :color="row.prop_consent == 1 ? 'green lighten-4' : 'orange lighten-4'" :text-color="row.prop_consent == 1 ? 'green darken-3' : 'orange darken-3'">
                           Proposer: {{ row.prop_consent == 1 ? 'Yes' : 'Pending' }}
                        </v-chip>
                        <v-chip x-small :color="row.sec_consent == 1 ? 'green lighten-4' : 'orange lighten-4'" :text-color="row.sec_consent == 1 ? 'green darken-3' : 'orange darken-3'">
                           Seconder: {{ row.sec_consent == 1 ? 'Yes' : 'Pending' }}
                        </v-chip>
                     </div>
                     <div class="mt-1">
                        <v-btn v-if="row.nom_consent != 1" x-small text color="primary" @click="resendEmail('nominee', row)">Re-send Nominee Email</v-btn>
                        <v-btn v-if="row.prop_consent != 1" x-small text color="primary" @click="resendEmail('proposer', row)">Re-send Proposer Email</v-btn>
                        <v-btn v-if="row.sec_consent != 1" x-small text color="primary" @click="resendEmail('seconder', row)">Re-send Seconder Email</v-btn>
                     </div>
                  </div>
             </div>

            </div>

            </v-card-text>
         </v-card>
      </v-dialog>
   </dashboard-layout>
</template>

<script>
import BaseBreadcrumb from '@/components/BaseBreadcrumb.vue'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
const axios = require('axios')

export default {
   name: 'MOANominations',
   components: { DashboardLayout, BaseBreadcrumb },
   data() {
      return {
         page: { title: 'Nominations', icon: 'mdi-account-multiple-plus' },
         breadcrumbs: [
            { text: 'Dashboard', disabled: false, to: '/dashboard' },
            { text: 'Nominations', disabled: true },
         ],
         loading: false,
         items: [],
         zones: [],
         membertypeOptions: ['ASC', 'STU', 'GRA', 'COR', 'FEL', 'HON', 'PAT'],
         headers: [
            { text: 'No.', value: 'no', width: 60, sortable: false },
            { text: 'Nomination Position', value: 'title', sortable: false },
            { text: 'Region / Zone', value: 'zonetitle', sortable: false },
            { text: 'Enabled', value: 'isenabled', width: 100, sortable: false },
            { text: 'Period', value: 'period', width: 180, sortable: false },
            { text: 'Action', value: 'action', width: 220, sortable: false },
         ],

         moduleEnabled: true,
         moduleLoading: false,

         formDialog: false,
         formSaving: false,
         formError: '',
         form: this.blankForm(),

         deleteDialog: false,
         deleting: false,
         deleteTarget: null,

         resultDialog: false,
         resultLoading: false,
         resultError: '',
         resultTitle: '',
         resultItems: [],
         /*resultHeaders: [
            { text: 'Rank', value: 'rank', width: 70, sortable: false },
            { text: 'Nominee', value: 'nominee', sortable: false },
            { text: 'Vote Count', value: 'votecount', width: 130, sortable: false },
            { text: 'Proposer', value: 'proposer', sortable: false },
            { text: 'Seconder', value: 'seconder', sortable: false },
            { text: 'Consent', value: 'consent', width: 150, sortable: false },
         ],*/
      }
   },
   mounted() {
      this.loadItems()
      this.loadZones()
      this.loadModuleStatus()
   },
   methods: {
      blankForm() {
         return {
            id: null,
            title: '',
            summary: '',
            description: '',
            rules: '',
            datestart: '',
            dateend: '',
            datewithdrawstart: '',
            datewithdrawend: '',
            age: 0,
            agerule: 'none',
            nomzone: null,
            membertypes: [],
            isenabled: true,
            isonlycouncilmembers: false,
            isnonmandatory: true,
            filtercnddtype: null,
         }
      },
      loadItems() {
         this.loading = true
         axios.get('/admin/nominations/list')
            .then((resp) => {
               // Backend already returns ORDER BY id DESC (latest first)
               this.items = resp.data && resp.data.status === 'OK' ? (resp.data.data || []) : []
            })
            .finally(() => {
               this.loading = false
            })
      },
      loadZones() {
         axios.get('/admin/zone/ddlist')
            .then((resp) => {
               this.zones = resp.data && resp.data.status === 'OK' ? (resp.data.data || []) : []
            })
            .catch(() => { this.zones = [] })
      },
      loadModuleStatus() {
         this.moduleLoading = true
         axios.get('/admin/modules/nomination')
            .then((resp) => {
               if (resp.data && resp.data.status === 'OK' && resp.data.data) {
                  this.moduleEnabled = !!Number(resp.data.data.isenabled)
               }
            })
            .finally(() => { this.moduleLoading = false })
      },
      toggleModule(value) {
         this.moduleLoading = true
         axios.post('/admin/modules/nomination', { isenabled: value ? 1 : 0 })
            .catch(() => {
               this.moduleEnabled = !value
            })
            .finally(() => { this.moduleLoading = false })
      },
      formatDate(value) {
         if (!value) return '-'
         const date = new Date(value)
         if (isNaN(date.getTime())) return value
         return date.toLocaleDateString('en-GB') + ' ' + date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
      },
      toDatetimeLocal(value) {
         if (!value) return ''
         const date = new Date(value)
         if (isNaN(date.getTime())) return ''
         const pad = (n) => String(n).padStart(2, '0')
         return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + 'T' + pad(date.getHours()) + ':' + pad(date.getMinutes())
      },
      openNew() {
         this.form = this.blankForm()
         this.formError = ''
         this.formDialog = true
      },
      openEdit(item) {
         this.form = {
            id: item.id,
            title: item.title,
            summary: item.summary,
            description: item.description,
            rules: item.rules,
            datestart: this.toDatetimeLocal(item.datestart),
            dateend: this.toDatetimeLocal(item.dateend),
            datewithdrawstart: this.toDatetimeLocal(item.datewithdrawstart),
            datewithdrawend: this.toDatetimeLocal(item.datewithdrawend),
            age: item.age || 0,
            agerule: item.agerule || 'none',
            nomzone: item.nomzone,
            membertypes: (item.membertypes || '').split(',').map((x) => x.trim()).filter(Boolean),
            isenabled: !!Number(item.isenabled),
            isonlycouncilmembers: !!Number(item.isonlycouncilmembers),
            isnonmandatory: !!Number(item.isnonmandatory),
            filtercnddtype: item.filtercnddtype,
         }
         this.formError = ''
         this.formDialog = true
      },
      saveForm() {
         if (!this.form.title || !this.form.datestart || !this.form.dateend || !this.form.datewithdrawstart || !this.form.datewithdrawend || this.form.membertypes.length === 0) {
            this.formError = 'Please fill in title, all dates, and at least one member type.'
            return
         }

         const payload = new FormData()
         if (this.form.id) payload.append('id', this.form.id)
         payload.append('title', this.form.title)
         payload.append('summary', this.form.summary || '')
         payload.append('description', this.form.description || '')
         payload.append('rules', this.form.rules || '')
         payload.append('datestart', this.form.datestart)
         payload.append('dateend', this.form.dateend)
         payload.append('datewithdrawstart', this.form.datewithdrawstart)
         payload.append('datewithdrawend', this.form.datewithdrawend)
         payload.append('age', this.form.age || 0)
         payload.append('agerule', this.form.agerule || 'none')
         payload.append('nomzone', this.form.nomzone || '')
         payload.append('membertypes', this.form.membertypes.join(','))
         payload.append('isenabled', this.form.isenabled ? 1 : 0)
         payload.append('isonlycouncilmembers', this.form.isonlycouncilmembers ? 1 : 0)
         payload.append('isnonmandatory', this.form.isnonmandatory ? 1 : 0)
         payload.append('filtercnddtype', this.form.filtercnddtype || 'null')

         this.formSaving = true
         const url = this.form.id ? '/admin/nominations/update' : '/admin/nominations/add'
         axios.post(url, payload)
            .then((resp) => {
               if (resp.data && resp.data.status === 'OK') {
                  this.formDialog = false
                  this.loadItems()
               } else {
                  this.formError = (resp.data && resp.data.error) || 'Unable to save record.'
               }
            })
            .catch(() => { this.formError = 'Unable to save record.' })
            .finally(() => { this.formSaving = false })
      },
      confirmDelete(item) {
         this.deleteTarget = item
         this.deleteDialog = true
      },
      doDelete() {
         if (!this.deleteTarget) return
         this.deleting = true
         axios.post('/admin/nominations/delete', { id: this.deleteTarget.id })
            .then(() => {
               this.deleteDialog = false
               this.loadItems()
            })
            .finally(() => { this.deleting = false })
      },
      viewResult(item) {
         this.resultTitle = item.title
         this.resultDialog = true
         this.resultLoading = true
         this.resultError = ''
         this.resultItems = []
         axios.get('/admin/nomination/result/' + item.id)
            .then((resp) => {
               if (resp.data && resp.data.status === 'OK') {
                  this.resultItems = resp.data.data || []
               } else {
                  this.resultError = (resp.data && resp.data.error) || 'Unable to load result.'
               }
            })
            .catch(() => {
               this.resultError = 'Unable to load result.'
            })
            .finally(() => {
               this.resultLoading = false
            })
      },
      cancelWithdrawal(item) {
         if (!confirm('Cancel withdrawal for ' + item.nom_name + '?')) return
         axios.post('/admin/nomination/cancel_withdrawal', { id: item.id })
            .then((resp) => {
               if (resp.data && resp.data.status === 'OK') {
                  item.nom_datewithdrawal = null
               } else {
                  alert((resp.data && resp.data.error) || 'Failed to cancel withdrawal.')
               }
            })
            .catch(() => alert('Failed to cancel withdrawal.'))
      },
      resendEmail(type, item) {
         const url = type === 'nominee' ? '/admin/nomination/resend_nominee'
            : type === 'proposer' ? '/admin/nomination/resend_proposer'
            : '/admin/nomination/resend_seconder'
         axios.post(url, { id: item.id })
            .then((resp) => {
               alert((resp.data && resp.data.status === 'OK') ? 'Email sent.' : (resp.data.error || 'Failed to send.'))
            })
            .catch(() => alert('Failed to send email.'))
      },
   },

   computed: {
      activeNominations() {
         const active = this.resultItems.filter((item) => {
            if (item.nom_datewithdrawal) return false
            if (item.nom_consent == 0) return false
            if (item.prop_consent == 0) return false
            if (item.sec_consent == 0) return false
            return true
         })
         const groups = {}
         active.forEach((item) => {
            if (!groups[item.nom_id]) {
               groups[item.nom_id] = {
                  nom_id: item.nom_id,
                  nom_name: item.nom_name,
                  nom_tac: item.nom_tac,
                  nom_consent: item.nom_consent,
                  nom_dateconsent: item.nom_dateconsent,
                  vote_count: 0,
                  fully_consented_count: 0,
                  rows: []
               }
            }
            const group = groups[item.nom_id]
            group.vote_count++
            // Row ini dikira "fully consented" bila nominee + proposer + seconder SEMUA consent = 1
            const rowFullyConsented = (item.nom_consent == 1 && item.prop_consent == 1 && item.sec_consent == 1)
            if (rowFullyConsented) group.fully_consented_count++
            group.rows.push(item)
         })
         return Object.values(groups)
            .map((g) => ({ ...g, is_fully_consented: g.fully_consented_count > 0 }))
            .sort((a, b) => b.vote_count - a.vote_count)
      },
      withdrawnNominations() {
         return this.resultItems.filter((item) => !!item.nom_datewithdrawal)
      },
      pendingNominations() {
         const pending = this.resultItems.filter((item) => {
            if (item.nom_datewithdrawal) return false
            if (item.nom_consent == 1 && item.prop_consent == 1 && item.sec_consent == 1) return false
            return true
         })
         const groups = {}
         pending.forEach((item) => {
            if (!groups[item.nom_id]) {
               groups[item.nom_id] = {
                  nom_id: item.nom_id,
                  nom_name: item.nom_name,
                  nom_tac: item.nom_tac,
                  rows: []
               }
            }
            groups[item.nom_id].rows.push(item)
         })
         return Object.values(groups)
      },
   },
}
</script>

